import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Phone } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { truncateText } from "@/lib/utils";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { userAPI } from "../Services/apiService";

import {
  Users,
  Search,
  Edit,
  Trash2,
  UserPlus,
  Shield,
  Building,
  Mail,
  MoreHorizontal,
  RefreshCw,
  AlertTriangle,
  Upload,
  Download,
  Briefcase,
} from "lucide-react";
import ResponsiveTooltip from "./ui/ResponsiveTooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { showToast } from "../utils/toast";
import { handleAPIError } from "../utils/errorHandler";
import { formatPhoneForDisplay } from "../utils/phoneFormatter";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import * as XLSX from "xlsx";
import BulkUploadModal from "./BulkUploadModal";
import Pagination from "./Pagination";
import { normalizePaginatedResponse } from "../lib/pagination";
import useDebouncedValue from "../hooks/useDebouncedValue";

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [filterRole, setFilterRole] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const isInitialLoad = useRef(true);

  const usersRequestRef = useRef(null);

  const closeBulkUploadModal = () => {
    setIsBulkUploadModalOpen(false);
    handleRefreshData();
  };

  // Load users with server-side pagination (debounced search)
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser?.token) return;

      try {
        // Only show loading on initial load, not on filter changes
        if (isInitialLoad.current) {
          setLoading(true);
          isInitialLoad.current = false;
        }
        if (usersRequestRef.current) {
          usersRequestRef.current.abort();
        }
        const controller = new AbortController();
        usersRequestRef.current = controller;

        // Determine include_vendors based on filterRole for user list
        const shouldIncludeVendors =
          filterRole === "vendor" || filterRole === "";

        // OPTIMIZED: Execute sequentially to reduce concurrent requests
        // First fetch users, then stats (stats is non-critical, can fail silently)
        const usersResponse = await userAPI.getUsers(
          {
            page,
            page_size: pageSize,
            search: debouncedSearch,
            role: filterRole || undefined,
            include_vendors: shouldIncludeVendors,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
          { signal: controller.signal }
        );

        // Fetch stats separately (non-blocking, can fail silently)
        const statsResponse = await userAPI
          .getUserStats({ include_vendors: true })
          .catch(() => null);

        // Backend now handles pagination and sorting server-side
        // If response is paginated (has items, total, etc.), use it directly
        // Otherwise, use normalizePaginatedResponse for backward compatibility
        let norm;
        if (
          usersResponse.data &&
          typeof usersResponse.data === "object" &&
          "items" in usersResponse.data
        ) {
          // Server-side paginated response - use directly (sorting already done on server)
          norm = {
            items: usersResponse.data.items || [],
            total: usersResponse.data.total || 0,
            page: usersResponse.data.page || page,
            page_size: usersResponse.data.page_size || pageSize,
            pages: usersResponse.data.pages || 1,
            has_next: usersResponse.data.has_next || false,
            has_prev: usersResponse.data.has_prev || false,
          };
        } else {
          // Backward compatibility: client-side pagination/sorting
          norm = normalizePaginatedResponse(
            usersResponse.data,
            page,
            pageSize,
            sortBy,
            sortOrder,
            debouncedSearch,
            filterRole || ""
          );
        }

        // Debug: Check if created_at is coming from API
        console.log("Users API Response (first user):", norm.items[0]);

        setAllUsers(norm.items);
        setTotal(norm.total);
        setPages(norm.pages);
        setHasNext(norm.has_next);
        setHasPrev(norm.has_prev);
        setUserStats(statsResponse?.data || null);
      } catch (error) {
        // Ignore CanceledError - it's expected when requests are cancelled
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        handleAPIError(error, "Failed to load users", true);
      } finally {
        // Only set loading to false if request wasn't cancelled
        if (usersRequestRef.current?.signal?.aborted !== true) {
          setLoading(false);
        }
      }
    };
    loadData();
    return () => {
      if (usersRequestRef.current) {
        usersRequestRef.current.abort();
      }
    };
  }, [
    currentUser?.token,
    page,
    pageSize,
    debouncedSearch,
    filterRole,
    sortBy,
    sortOrder,
  ]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, filterRole]);

  // Handle manual refresh
  const handleRefreshData = async () => {
    try {
      console.log("Loading users...");
      setLoading(true);
      // OPTIMIZED: Execute sequentially to reduce concurrent requests
      const usersResponse = await userAPI.getUsers({
        page,
        page_size: pageSize,
        search: debouncedSearch,
        role: filterRole || undefined,
        include_vendors:
          filterRole === "vendor" || filterRole === "" ? true : false,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      // Fetch stats separately (non-blocking, can fail silently)
      const statsResponse = await userAPI
        .getUserStats({ include_vendors: true })
        .catch(() => null);

      const norm = normalizePaginatedResponse(
        usersResponse.data,
        page,
        pageSize,
        sortBy,
        sortOrder,
        debouncedSearch,
        filterRole || ""
      );
      setAllUsers(norm.items);
      setTotal(norm.total);
      setPages(norm.pages);
      setHasNext(norm.has_next);
      setHasPrev(norm.has_prev);
      setUserStats(statsResponse?.data || null);

      showToast.success("Users refreshed successfully");
    } catch (error) {
      handleAPIError(error, "Failed to refresh users", true);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers;

  const handleCreateUser = async (userData) => {
    try {
      const response = await userAPI.createUser(userData);
      setAllUsers((prev) => [...prev, response.data]);

      // Update stats locally instead of API call
      setUserStats((prev) =>
        prev
          ? {
            ...prev,
            total_users: prev.total_users + 1,
            vendors:
              response.data.role === "vendor"
                ? (prev.vendors || 0) + 1
                : prev.vendors,
            [response.data.role + "s"]:
              (prev[response.data.role + "s"] || 0) + 1,
          }
          : null
      );

      showToast.success("User created successfully");
      setIsCreateUserModalOpen(false);
    } catch (error) {
      handleAPIError(error, "Failed to create user", true);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const response = await userAPI.updateUser(userId, userData);

      setAllUsers((prev) =>
        prev.map((user) => (user.id === userId ? response.data : user))
      );

      // Update stats locally if role changed
      const oldUser = allUsers.find((u) => u.id === userId);
      if (oldUser && oldUser.role !== response.data.role) {
        setUserStats((prev) =>
          prev
            ? {
              ...prev,
              vendors:
                oldUser.role === "vendor"
                  ? Math.max(0, (prev.vendors || 0) - 1)
                  : response.data.role === "vendor"
                    ? (prev.vendors || 0) + 1
                    : prev.vendors,
              [oldUser.role + "s"]: Math.max(
                0,
                (prev[oldUser.role + "s"] || 0) - 1
              ),
              [response.data.role + "s"]:
                (prev[response.data.role + "s"] || 0) + 1,
            }
            : null
        );
      }

      showToast.success("User updated successfully");
      setIsEditUserModalOpen(false);
      window.dispatchEvent(
        new CustomEvent("userUpdated", { detail: { userId: response.data.id } })
      );
    } catch (error) {
      handleAPIError(error, "Failed to update user", true);
    }
  };

  const handleDeleteUser = (userId) => {
    const user = allUsers.find((u) => u.id === userId);
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      // Call delete API (soft delete)
      await userAPI.deleteUser(userToDelete.id);

      // Remove from local state
      setAllUsers((prev) => prev.filter((user) => user.id !== userToDelete.id));

      // Update stats
      setUserStats((prev) =>
        prev
          ? {
            ...prev,
            total_users: prev.total_users - 1,
            vendors:
              userToDelete.role === "vendor"
                ? Math.max(0, (prev.vendors || 0) - 1)
                : prev.vendors,
            [userToDelete.role + "s"]: Math.max(
              0,
              (prev[userToDelete.role + "s"] || 0) - 1
            ),
          }
          : null
      );

      showToast.success(
        `User "${userToDelete.full_name || userToDelete.email
        }" deleted successfully`
      );
      setShowDeleteConfirm(false);
      setUserToDelete(null);

      // Trigger custom event for other components
      window.dispatchEvent(
        new CustomEvent("userDeleted", { detail: { userId: userToDelete.id } })
      );
    } catch (error) {
      handleAPIError(error, "Failed to delete user", true);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const handleToggleUser = async (userId) => {
    const user = allUsers.find((u) => u.id === userId);
    if (!user) return;

    // Prevent toggling admin users
    if (user.role === "admin") {
      showToast.error("Admin accounts cannot be disabled");
      return;
    }

    try {
      const newActiveStatus = !user.is_active;
      const response = await userAPI.toggleUserActive(userId);

      // Update user in local state with response data
      setAllUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, is_active: response.data.is_active } : u
        )
      );

      showToast.success(
        `User ${newActiveStatus ? "activated" : "deactivated"} successfully`
      );

      // Trigger refresh event for other components
      window.dispatchEvent(
        new CustomEvent("userStatusChanged", {
          detail: { userId, isActive: response.data.is_active },
        })
      );
    } catch (error) {
      handleAPIError(error, "Failed to toggle user status", true);
    }
  };

  const handleExportToExcel = async () => {
    try {
      showToast.info("Preparing Excel file...");

      // Fetch all users for export using pagination (backend limit is 200 per request)
      const allUsers = [];
      let currentPage = 1;
      const pageSize = 200; // Maximum allowed by backend
      let hasMore = true;
      let totalUsers = 0;

      // Fetch users in batches until all are retrieved
      // Note: Export includes all users (respects role filter but not search filter for complete export)
      while (hasMore) {
        const response = await userAPI.getUsers({
          page: currentPage,
          page_size: pageSize,
          // Don't apply search filter for export - export all users (or all matching role filter)
          search: undefined,
          role: filterRole || undefined,
          include_vendors:
            filterRole === "vendor" || filterRole === "" ? true : false,
        });

        const responseData = response.data;
        let users = [];

        // Handle both paginated and non-paginated responses
        if (responseData?.items && Array.isArray(responseData.items)) {
          // Paginated response
          users = responseData.items;
          totalUsers = responseData.total || 0;
          hasMore = currentPage < (responseData.pages || 1);
        } else if (Array.isArray(responseData)) {
          // Non-paginated response (backward compatibility)
          users = responseData;
          hasMore = users.length === pageSize; // If we got full page, might be more
        } else {
          // Fallback
          users = [];
          hasMore = false;
        }

        allUsers.push(...users);

        // If we got fewer users than page size, we've reached the end
        if (users.length < pageSize) {
          hasMore = false;
        } else {
          currentPage++;
        }

        // Safety limit: prevent infinite loops (max 100 pages = 20,000 users)
        if (currentPage > 100) {
          console.warn("Export reached safety limit of 100 pages");
          break;
        }
      }

      const usersToExport = allUsers;

      if (usersToExport.length === 0) {
        showToast.warning("No users to export");
        return;
      }

      // Prepare data for Excel with proper null/undefined handling
      const excelData = usersToExport.map((user, index) => {
        // Safely format role with null/undefined checks
        let roleDisplay = "N/A";
        if (
          user.role &&
          typeof user.role === "string" &&
          user.role.length > 0
        ) {
          roleDisplay = user.role.charAt(0).toUpperCase() + user.role.slice(1);
        }

        // Safely format status with null/undefined checks
        let statusDisplay = "Unknown";
        if (user.is_active !== null && user.is_active !== undefined) {
          statusDisplay = user.is_active ? "Active" : "Inactive";
        }

        return {
          "Sr. No.": index + 1,
          Name: user.full_name || "N/A",
          Email: user.email || "N/A",
          Phone: user.phone_number ? String(user.phone_number) : "N/A",
          Role: roleDisplay,
          Status: statusDisplay,
          "Created Date": user.created_at
            ? new Date(user.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
            : "N/A",
          "Created Time": user.created_at
            ? new Date(user.created_at).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            : "N/A",
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws["!cols"] = [
        { wch: 8 }, // Sr. No.
        { wch: 25 }, // Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 12 }, // Role
        { wch: 10 }, // Status
        { wch: 15 }, // Created Date
        { wch: 12 }, // Created Time
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Users");

      // Generate filename with current date
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `Users_Export_${timestamp}.xlsx`;

      // Write and download file
      XLSX.writeFile(wb, filename);

      showToast.success(`Exported ${usersToExport.length} users successfully`);
    } catch (error) {
      console.error("Export error:", error);
      handleAPIError(error, "Failed to export users", true);
    }
  };

  const handleBulkUpload = async (file) => {
    try {
      const response = await userAPI.bulkUploadUsers(file);
      showToast.success(
        `Bulk upload completed: ${response.data.created} users created`
      );
      setIsBulkUploadModalOpen(false);

      // OPTIMIZED: Execute sequentially to reduce concurrent requests
      const usersResponse = await userAPI.getUsers({ limit: 100 });
      const statsResponse = await userAPI.getUserStats().catch(() => null);
      setAllUsers(usersResponse.data || []);
      setUserStats(statsResponse?.data || null);
    } catch (error) {
      handleAPIError(error, "Failed to bulk upload users", true);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 border-0 hover:bg-red-100 hover:text-red-800";
      case "business_head":
        return "bg-blue-100 text-blue-800 border-0 hover:bg-blue-100 hover:text-blue-800";
      case "employee":
        return "bg-green-100 text-green-800 border-0 hover:bg-green-100 hover:text-green-800";
      case "vendor":
        return "bg-purple-100 text-purple-800 border-0 hover:bg-purple-100 hover:text-purple-800";
      default:
        return "bg-gray-100 text-gray-800 border-0 hover:bg-gray-100 hover:text-gray-800";
    }
  };

  const formatRoleDisplay = (role) => {
    if (!role) return "";
    // Convert role to title case
    return role
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <Shield className="w-4 h-4" />;
      case "business_head":
        return <Building className="w-4 h-4" />;
      case "employee":
        return <Users className="w-4 h-4" />;
      case "vendor":
        return <Briefcase className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-yellow-600" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="loading-shimmer h-8 w-64 rounded-lg"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="loading-shimmer h-24 rounded-xl"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-0 ">
      <CardHeader>
        <CardTitle className="flex flex-col xl:flex-row gap-4 xl:gap-5 sm:flex ">
          <div>
            {userStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-gradient-to-r  h-fit from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                  <div className="flex   items-center justify-between">
                    <div className=" ">
                      <div className="flex gap-2 items-center">
                        <p className="text-sm sm:text-nowrap font-medium text-yellow-600">
                          Total Users
                        </p>
                        <Users className="w-4 h-4 text-yellow-600" />
                      </div>
                      <p className="text-xl font-bold text-yellow-900">
                        {userStats.total_users}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                  <div className="flex   items-center justify-between">
                    <div className=" ">
                      <div className="flex gap-2 items-center">
                        <p className="text-sm sm:text-nowrap font-medium text-red-600">
                          Admins
                        </p>
                        <Shield className="w-4 h-4 text-red-600" />
                      </div>
                      <p className="text-xl font-bold text-red-900">
                        {userStats.admins}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <div className="flex   items-center justify-between">
                    <div className=" ">
                      <div className="flex gap-2 items-center">
                        <p className="text-sm sm:text-nowrap font-medium text-blue-600">
                          Business Heads
                        </p>
                        {/* <Building className="w-4 h-4 text-blue-600" /> */}
                      </div>
                      <p className="text-xl font-bold text-blue-900">
                        {userStats.business_heads}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <div className="flex   items-center justify-between">
                    <div className=" ">
                      <div className="flex gap-1 sm:gap-2 items-center">
                        <p className="text-sm sm:text-nowrap font-medium text-green-600">
                          Employees
                        </p>
                        <Users className="w-4 h-4 text-green-600" />
                      </div>
                      <p className="text-xl font-bold text-green-900">
                        {userStats.employees}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-100 p-4 rounded-lg border border-purple-200">
                  <div className="flex gap-1 sm:gap-2 items-center">
                    <p className="text-sm sm:text-nowrap font-medium text-purple-600">
                      Vendors
                    </p>
                    <Users className="w-4 h-4 text-purple-600" />
                  </div>
                  <p className="text-xl font-bold text-purple-900">
                    {userStats.vendors}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className=" flex flex-col sm:flex-col gap-3">
            <div className="flex gap-3">
              <Button
                onClick={() => setIsCreateUserModalOpen(true)}
                className="flex w-full items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Add User
              </Button>

              <Button
                variant="outline"
                onClick={() => setIsBulkUploadModalOpen(true)}
                className="flex w-full font-normal items-center gap-1"
              >
                <Upload className="w-4 h-4 text-yellow-600" />
                Bulk Upload
              </Button>

              <Button
                variant="outline"
                onClick={handleExportToExcel}
                className="sm:flex w-full hidden font-normal items-center gap-2 border-green-200 hover:bg-green-50"
              >
                <Download className="w-4  h-4 font-normal text-green-600" />
                Export
              </Button>
            </div>

            <div className="hidden sm:block">
              <div className="flex  flex-col sm:flex-row gap-4">
                <div className="flex ">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      className="pl-10  xl:w-48 sm:w-full font-normal  focus-gold"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Select
                    value={filterRole || "all"}
                    onValueChange={(v) => {
                      setFilterRole(v === "all" ? "" : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-40 font-normal focus-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="business_head">
                        Business Head
                      </SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="block sm:hidden">
              <div className="flex  sm:flex-row gap-4">
                <div className="flex gap-3 w-full">
                  <Select
                    className=""
                    value={filterRole || "all"}
                    onValueChange={(v) => {
                      setFilterRole(v === "all" ? "" : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full font-normal focus-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="business_head">
                        Business Head
                      </SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={handleExportToExcel}
                  className=" w-full   items-center font-normal gap-2 border-green-200 hover:bg-green-50"
                >
                  <Download className="w-4 h-4  text-green-600" />
                  Export
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 sm:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                className="pl-10  w-full font-normal focus-gold"
              />
            </div>
          </div>
        </CardTitle>

        <div></div>
      </CardHeader>

      <CardContent className="space-y-1">
        {/* Stats Cards */}

        {/* Action Buttons */}

        {/* Filters */}

        {/* Users Display */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
            <p className="text-sm text-gray-400 mt-1">
              Try adjusting your search criteria
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block rounded-lg border overflow-hidden">
              <div className="max-h-[calc(100vh-300px)] overflow-auto">
                <div className="relative w-full overflow-visible">
                  <table className="w-full caption-bottom text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                      <tr className="bg-gray-100 border-b">
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <span>
                            Sr. <br></br>No.
                          </span>
                        </th>
                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSortBy("full_name");
                              setSortOrder(
                                sortBy === "full_name" && sortOrder === "asc"
                                  ? "desc"
                                  : "asc"
                              );
                              setPage(1);
                            }}
                            className="cursor-pointer text-left w-full flex items-center gap-1 hover:bg-gray-100 transition-colors"
                          >
                            <span className="text-center">User</span>
                            <span
                              className={
                                sortBy === "full_name"
                                  ? "text-gray-600 text-xs"
                                  : "text-gray-400 text-xs"
                              }
                            >
                              {sortBy === "full_name"
                                ? sortOrder === "asc"
                                  ? "▲"
                                  : "▼"
                                : "▼"}
                            </span>
                          </button>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSortBy("email");
                              setSortOrder(
                                sortBy === "email" && sortOrder === "asc"
                                  ? "desc"
                                  : "asc"
                              );
                              setPage(1);
                            }}
                            className="cursor-pointer text-left w-full flex items-center gap-1 hover:bg-gray-100 transition-colors"
                          >
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>Email</span>
                            <span
                              className={
                                sortBy === "email"
                                  ? "text-gray-600 text-xs"
                                  : "text-gray-400 text-xs"
                              }
                            >
                              {sortBy === "email"
                                ? sortOrder === "asc"
                                  ? "▲"
                                  : "▼"
                                : "▼"}
                            </span>
                          </button>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSortBy("phone_number");
                              setSortOrder(
                                sortBy === "phone_number" && sortOrder === "asc"
                                  ? "desc"
                                  : "asc"
                              );
                              setPage(1);
                            }}
                            className="cursor-pointer text-left w-full flex items-center gap-1 hover:bg-gray-100 transition-colors"
                          >
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>Phone</span>
                            <span
                              className={
                                sortBy === "phone_number"
                                  ? "text-gray-600 text-xs"
                                  : "text-gray-400 text-xs"
                              }
                            >
                              {sortBy === "phone_number"
                                ? sortOrder === "asc"
                                  ? "▲"
                                  : "▼"
                                : "▼"}
                            </span>
                          </button>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <span>Role</span>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSortBy("created_at");
                              setSortOrder(
                                sortBy === "created_at" && sortOrder === "asc"
                                  ? "desc"
                                  : "asc"
                              );
                              setPage(1);
                            }}
                            className="cursor-pointer text-left w-full flex items-center gap-1 hover:bg-gray-100 transition-colors"
                          >
                            <span>Created At</span>
                            <span
                              className={
                                sortBy === "created_at"
                                  ? "text-gray-600 text-xs"
                                  : "text-gray-400 text-xs"
                              }
                            >
                              {sortBy === "created_at"
                                ? sortOrder === "asc"
                                  ? "▲"
                                  : "▼"
                                : "▼"}
                            </span>
                          </button>
                        </th>
                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {filteredUsers.map((user, index) => (
                        <tr
                          key={user.id}
                          className="border-b transition-colors hover:bg-gray-50"
                        >
                          <td className="p-2 align-middle">
                            {(page - 1) * pageSize + index + 1}
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-yellow-100 text-yellow-800">
                                  {user.full_name?.charAt(0) ||
                                    user.email?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {user.full_name && user.full_name.length > 20 ? (
                                    <ResponsiveTooltip
                                      content={<p className="text-sm whitespace-pre-wrap">{user.full_name}</p>}
                                      className="max-w-md p-3"
                                    >
                                      <span className="cursor-pointer">
                                        {truncateText(user.full_name, 20)}
                                      </span>
                                    </ResponsiveTooltip>
                                  ) : (
                                    user.full_name || '-'
                                  )}
                                </p>
                                {/* <p className="text-xs text-gray-500">ID: {user.id}</p> */}
                              </div>
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center gap-2">
                              {user.email && user.email.length > 20 ? (
                                <ResponsiveTooltip
                                  content={<p className="text-sm whitespace-pre-wrap">{user.email}</p>}
                                  className="max-w-md p-3"
                                >
                                  <span className="text-sm cursor-pointer">
                                    {truncateText(user.email, 20)}
                                  </span>
                                </ResponsiveTooltip>
                              ) : (
                                <span className="text-sm">{user.email || '-'}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {formatPhoneForDisplay(user.phone_number)}
                              </span>
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <Badge
                              className={`${getRoleBadgeColor(
                                user.role
                              )} text-xs flex items-center cursor-pointer gap-1 w-fit`}
                            >
                              {getRoleIcon(user.role)}
                              {formatRoleDisplay(user.role)}
                            </Badge>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-700">
                                {user.created_at
                                  ? new Date(
                                    user.created_at
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                  : "N/A"}
                              </span>
                              {user.created_at && (
                                <span className="text-xs text-gray-500">
                                  {new Date(user.created_at).toLocaleTimeString(
                                    "en-US",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    }
                                  )}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 align-middle">
                            <div className="flex gap-2">
                              <ResponsiveTooltip
                                content={<p className="text-xs">Edit User</p>}
                              >
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setIsEditUserModalOpen(true);
                                  }}
                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4 text-blue-600" />
                                </Button>
                              </ResponsiveTooltip>

                              {/* Hide toggle for admin users */}
                              {user.role !== "admin" && (
                                <ResponsiveTooltip
                                  content={
                                    <p className="text-xs">
                                      {user.is_active
                                        ? "Disable User"
                                        : "Enable User"}
                                    </p>
                                  }
                                >
                                  <span
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex text-nowrap items-center"
                                  >
                                    <Switch
                                      checked={user.is_active ?? true}
                                      onCheckedChange={() =>
                                        handleToggleUser(user.id)
                                      }
                                    />
                                  </span>
                                </ResponsiveTooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="hidden sm:block">
              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={pageSize}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onPageChange={(p) => setPage(Math.max(1, p))}
                onPageSizeChange={(s) => {
                  setPageSize(Number(s));
                  setPage(1);
                }}
              />
            </div>

            {/* Mobile Accordion View */}
            <div className="md:hidden">
              <Accordion type="multiple" className="w-full">
                {filteredUsers.map((user) => (
                  <AccordionItem
                    key={user.id}
                    value={`user-${user.id}`}
                    className="border border-gray-200 rounded-lg mb-2"
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center space-x-3 flex-1 text-left">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-yellow-100 text-yellow-800">
                            {user.full_name?.charAt(0) || user.email?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">
                            {user.full_name}
                          </p>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge
                              className={`${getRoleBadgeColor(
                                user.role
                              )} text-xs flex items-center gap-1 hover:bg-opacity-100`}
                            >
                              {getRoleIcon(user.role)}
                              {formatRoleDisplay(user.role)}
                            </Badge>
                            <Badge
                              variant={user.is_active ? "default" : "secondary"}
                              className={
                                user.is_active
                                  ? "bg-green-100 text-green-800 border-0 text-xs"
                                  : "bg-gray-100 text-gray-600 border-0 text-xs"
                              }
                            >
                              {user.is_active ? "Active" : "Disabled"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{user.email}</span>
                        </div>

                        {user.phone_number && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">
                              {formatPhoneForDisplay(user.phone_number)}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              Created:
                            </span>
                            <span className="text-sm text-gray-700">
                              {user.created_at
                                ? new Date(user.created_at).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                                : "N/A"}
                            </span>
                          </div>
                          {user.created_at && (
                            <span className="text-xs text-gray-500 ml-14">
                              {new Date(user.created_at).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          {/* Hide toggle for admin users */}
                          {user.role !== "admin" && (
                            <ResponsiveTooltip
                              content={
                                <p className="text-xs">
                                  {user.is_active
                                    ? "Disable User"
                                    : "Enable User"}
                                </p>
                              }
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {user.is_active ? "Disable" : "Enable"}:
                                </span>
                                <Switch
                                  checked={user.is_active ?? true}
                                  onCheckedChange={() =>
                                    handleToggleUser(user.id)
                                  }
                                />
                              </div>
                            </ResponsiveTooltip>
                          )}
                        </div>

                        <div className="flex gap-2 pt-2 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              setIsEditUserModalOpen(true);
                            }}
                            className="flex-1"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          {/* Hide delete button for admin users */}
                          {user.role !== "admin" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(user.id)}
                              className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </>
        )}
        <div className="block sm:hidden">
          <Pagination
            page={page}
            pages={pages}
            total={total}
            pageSize={pageSize}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={(p) => setPage(Math.max(1, p))}
            onPageSizeChange={(s) => {
              setPageSize(Number(s));
              setPage(1);
            }}
          />
        </div>
      </CardContent>

      {/* Modals */}
      <CreateUserModal
        open={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSubmit={handleCreateUser}
      />

      <EditUserModal
        open={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        user={selectedUser}
        onSubmit={handleUpdateUser}
      />

      <BulkUploadModal
        open={isBulkUploadModalOpen}
        onClose={() => closeBulkUploadModal()}
        onSubmit={handleBulkUpload}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete this user? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          {userToDelete && (
            <div className="py-4 space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h4 className="font-medium text-gray-900">
                      {userToDelete.full_name}
                    </h4>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {userToDelete.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {userToDelete.phone_number
                          ? formatPhoneForDisplay(userToDelete.phone_number)
                          : "No phone"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`${userToDelete.role === "admin"
                            ? "bg-red-100 text-red-800 border-0"
                            : userToDelete.role === "business_head"
                              ? "bg-blue-100 text-blue-800 border-0"
                              : "bg-green-100 text-green-800 border-0"
                          } text-xs`}
                      >
                        {formatRoleDisplay(userToDelete.role)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  This will permanently delete the user and all associated data,
                  including meetings and action points.
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setUserToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteUser}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserManagement;
